const API_BASE_URL = 'https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api';
const DYNAMODB_TABLE = 'EversetTable';

class EnhancedApiClient {
  async makeRequest(endpoint, options = {}) {
    try {
      // Get current user token from AWS Cognito
      const { Auth } = await import('aws-amplify');
      let authToken = '';
      let userId = '';
      
      try {
        const session = await Auth.currentSession();
        authToken = session.getIdToken().getJwtToken();
        const user = await Auth.currentAuthenticatedUser();
        userId = user.username;
      } catch (authError) {
        console.log('No authenticated user found');
      }
      
      const config = {
        headers: {
          ...(options.method && options.method !== 'GET' ? { 'Content-Type': 'application/json' } : {}),
          'Authorization': `Bearer ${authToken}`,
          ...options.headers
        },
        ...options
      };

      console.log(`Making ${options.method || 'GET'} request to: ${API_BASE_URL}${endpoint}`);
      const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`API Error ${response.status}:`, errorText);
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Events API
  async getEvents(clientId = null) {
    const params = clientId ? `?clientId=${clientId}` : '';
    return this.makeRequest(`/events${params}`);
  }

  async getEvent(eventId) {
    return this.makeRequest(`/events/${eventId}`);
  }

  async createEvent(eventData) {
    return this.makeRequest('/events', {
      method: 'POST',
      body: JSON.stringify(eventData)
    });
  }

  async updateEvent(eventId, updates) {
    return await this.makeRequest(`/events/${eventId}`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    });
  }

  // Users API
  async getUser(userId) {
    return this.makeRequest(`/users/${userId}`);
  }

  async createUser(userData) {
    return this.makeRequest('/users', {
      method: 'POST',
      body: JSON.stringify(userData)
    });
  }

  // Conversations API
  async createConversation(data) {
    return await this.makeRequest('/messages/conversations', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async getConversations(eventId, userId = null) {
    const params = new URLSearchParams();
    if (eventId) params.append('eventId', eventId);
    if (userId) params.append('userId', userId);
    return await this.makeRequest(`/messages/conversations?${params.toString()}`);
  }

  async getConversationMessages(conversationId) {
    return await this.makeRequest(`/messages/conversations/${conversationId}/messages`);
  }

  async sendConversationMessage(conversationId, messageData) {
    return await this.makeRequest(`/messages/conversations/${conversationId}/messages`, {
      method: 'POST',
      body: JSON.stringify(messageData)
    });
  }

  async markConversationAsRead(conversationId, userId) {
    return await this.makeRequest(`/messages/conversations/${conversationId}/read`, {
      method: 'PUT',
      body: JSON.stringify({ userId })
    });
  }

  // Keep old method names pointing to new endpoints so nothing breaks during migration
  async createThread(eventId, taskId, threadData) {
    return await this.createConversation({
      eventId,
      participants: threadData.participants || [],
      createdBy: threadData.createdBy || threadData.sender,
      message: threadData.question,
      taskId
    });
  }

  async getThreads(eventId, userId = null) {
    return await this.getConversations(eventId, userId);
  }

  async getMessages(conversationId) {
    return await this.getConversationMessages(conversationId);
  }

  async sendMessage(conversationId, messageData) {
    return await this.sendConversationMessage(conversationId, messageData);
  }

  async markThreadAsRead(conversationId, userId) {
    return await this.markConversationAsRead(conversationId, userId);
  }

  // Documents API
  async getDocuments(eventId) {
    return await this.makeRequest(`/documents?eventId=${eventId}`);
  }

  async uploadDocument(documentData) {
    return await this.makeRequest('/documents', {
      method: 'POST',
      body: JSON.stringify(documentData)
    });
  }

  async updateDocument(documentId, updates) {
    return await this.makeRequest(`/documents/${documentId}`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    });
  }

  async deleteDocument(documentId) {
    return await this.makeRequest(`/documents/${documentId}`, {
      method: 'DELETE'
    });
  }

  // Tasks API
  async getTasks(eventId) {
    return await this.makeRequest(`/events/${eventId}/tasks`);
  }

  async createTask(taskData) {
    const taskId = taskData.id || `task-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const taskWithId = { ...taskData, id: taskId };
    
    return await this.makeRequest(`/events/${taskData.eventId}/tasks`, {
      method: 'POST',
      body: JSON.stringify(taskWithId)
    });
  }

  async updateTask(eventId, taskId, updates) {
    return await this.makeRequest(`/events/${eventId}/tasks/${taskId}`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    });
  }

  // File upload for documents using presigned URLs
  async uploadFile(file) {
    try {
      console.log('Starting file upload for:', file.name);
      
      // Get presigned URL
      const presignedResponse = await this.makeRequest('/upload', {
        method: 'POST',
        body: JSON.stringify({
          fileName: file.name,
          fileType: file.type
        })
      });
      
      console.log('Presigned URL response:', presignedResponse);
      const { presignedUrl, fileUrl, fileName, fileKey } = presignedResponse;
      
      if (!presignedUrl) {
        throw new Error('No presigned URL received from server');
      }
      
      // Upload directly to S3 using presigned URL
      const uploadResponse = await fetch(presignedUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type
        }
      });
      
      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text();
        throw new Error(`S3 upload failed: ${uploadResponse.statusText} - ${errorText}`);
      }
      
      console.log('File uploaded successfully:', fileUrl);
      
      return {
        fileUrl,
        fileName,
        fileKey
      };
    } catch (error) {
      console.error('File upload failed:', error);
      throw error;
    }
  }

  // Invoices/Quotes API
  async getInvoices(eventId) {
    return await this.makeRequest(`/invoices?eventId=${eventId}`);
  }

  async getQuotes(eventId) {
    const invoices = await this.getInvoices(eventId);
    return invoices.filter(invoice => invoice.invoiceType === 'quote');
  }

  async createInvoice(invoiceData) {
    return await this.makeRequest('/invoices', {
      method: 'POST',
      body: JSON.stringify(invoiceData)
    });
  }

  async createQuote(quoteData) {
    return await this.createInvoice({ ...quoteData, invoiceType: 'quote' });
  }

  async updateInvoice(invoiceId, updates) {
    return await this.makeRequest(`/invoices/${invoiceId}`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    });
  }

  async updateQuote(quoteId, updates) {
    return await this.updateInvoice(quoteId, updates);
  }

  async deleteInvoice(invoiceId) {
    return await this.makeRequest(`/invoices/${invoiceId}`, {
      method: 'DELETE'
    });
  }

  // Notifications API
  async getNotifications(eventId) {
    return await this.makeRequest(`/notifications?eventId=${eventId}`);
  }

  async createNotification(notificationData) {
    return await this.makeRequest('/notifications', {
      method: 'POST',
      body: JSON.stringify(notificationData)
    });
  }

  // Timeline API
  async getTimeline(eventId) {
    return this.makeRequest(`/timeline?eventId=${eventId}`);
  }

  async createTimelineItem(timelineData) {
    return this.makeRequest('/timeline', {
      method: 'POST',
      body: JSON.stringify(timelineData)
    });
  }

  // Payments API
  async getPayments(eventId) {
    return this.makeRequest(`/payments?eventId=${eventId}`);
  }

  async createPaymentIntent(paymentData) {
    return this.makeRequest('/payments/create-intent', {
      method: 'POST',
      body: JSON.stringify(paymentData)
    });
  }

  async recordPayment(paymentData) {
    return this.makeRequest('/payments', {
      method: 'POST',
      body: JSON.stringify(paymentData)
    });
  }

  // Finance API (keeping localStorage for now as requested)
  async getFinanceApplications() {
    try {
      return await this.makeRequest('/finance/applications');
    } catch (error) {
      console.log('API not available, using localStorage fallback');
      return JSON.parse(localStorage.getItem('financeApplications') || '[]');
    }
  }

  async submitFinanceApplication(applicationData) {
    try {
      return await this.makeRequest('/finance/applications', {
        method: 'POST',
        body: JSON.stringify(applicationData)
      });
    } catch (error) {
      console.log('API not available, using localStorage fallback');
      const existingApplications = JSON.parse(localStorage.getItem('financeApplications') || '[]');
      existingApplications.push(applicationData);
      localStorage.setItem('financeApplications', JSON.stringify(existingApplications));
      return { success: true, requestId: applicationData.requestId };
    }
  }

  async updateFinanceApplication(requestId, updates) {
    try {
      return await this.makeRequest(`/finance/applications/${requestId}`, {
        method: 'PUT',
        body: JSON.stringify(updates)
      });
    } catch (error) {
      console.log('API not available, using localStorage fallback');
      const applications = JSON.parse(localStorage.getItem('financeApplications') || '[]');
      const updatedApplications = applications.map(app => 
        app.requestId === requestId ? { ...app, ...updates } : app
      );
      localStorage.setItem('financeApplications', JSON.stringify(updatedApplications));
      return { success: true };
    }
  }


}

const enhancedApiClient = new EnhancedApiClient();
export default enhancedApiClient;